import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import LanguageSwitcher from './LanguageSwitcher';
import { categoryList } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  const langCode = i18n.language || 'en';

  const scrollToSection = (hash: string) => {
    const id = hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNavigate = (path: string) => {
    setMobileOpen(false);
    setMobileCategoriesOpen(false);

    const isAnchor = path.includes('#');
    const hash = isAnchor ? path.split('#')[1] : '';
    const basePath = isAnchor ? path.split('#')[0] : path;

    const currentBase = location.pathname.replace(new RegExp(`^/${langCode}`), '');
    const targetBase = basePath.replace(new RegExp(`^/${langCode}`), '');

    const onHome = targetBase === '/' || targetBase === '';
    const alreadyHome = currentBase === '/' || currentBase === '';

    if (isAnchor && onHome && alreadyHome) {
      // Already on home, just scroll to anchor
      scrollToSection(hash);
      return;
    }

    if (isAnchor && onHome && !alreadyHome) {
      // Navigate to home first, then scroll after mount
      navigate(`/${langCode}/`);
      setTimeout(() => scrollToSection(hash), 400);
      return;
    }

    // Normal page navigation
    navigate(`/${langCode}${basePath}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navLinks = [
    { key: 'nav.home', path: '/' },
    { key: 'nav.categories', path: '/#categories', isAnchor: true },
    { key: 'nav.guides', path: '/#guides', isAnchor: true },
    { key: 'nav.shoppingGuide', path: '/blog/cssbuy-buying-guide' },
    { key: 'nav.about', path: '/about/' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-background-200 shadow-sm">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo */}
          <Link
            to={`/${langCode}/`}
            className="flex items-center gap-2 shrink-0"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <span className="font-heading text-lg md:text-xl font-bold tracking-tight text-foreground-950 whitespace-nowrap">
              CSSBuy<span className="text-primary-600"> Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.key}
                type="button"
                onClick={() => handleNavigate(link.path)}
                className="px-3 py-2 rounded-md text-sm font-semibold text-foreground-800 hover:text-primary-600 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                {t(link.key)}
              </button>
            ))}
            <div className="w-px h-5 bg-background-300 mx-2" />
            <LanguageSwitcher />
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden p-2 rounded-md text-foreground-900 hover:bg-background-100"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl w-6 h-6 flex items-center justify-center`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.key}
                type="button"
                onClick={() => handleNavigate(link.path)}
                className="block w-full text-left px-3 py-3 rounded-md text-base font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                {t(link.key)}
              </button>
            ))}

            {/* Mobile Categories */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-md text-base font-semibold text-primary-600 hover:bg-background-100 transition-colors"
              >
                <span>{t('nav.categories')}</span>
                <i className={`${mobileCategoriesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} w-5 h-5 flex items-center justify-center`} />
              </button>
              {mobileCategoriesOpen && (
                <div className="grid grid-cols-2 gap-1 pl-2 pr-1 pb-2">
                  {categoryList.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-1.5 px-2 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0" />
                      {t(`categories.items.${cat.key}`)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-background-200">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}